import { useEffect, useState } from "react";
import { fetchAdminSettings, adminUpdateSettings } from "@/lib/api";
import { DEVI_FORMS } from "@/lib/deviForms";
import { MAHAVIDYAS } from "@/lib/mahavidyas";
import ImageUploader from "@/components/ImageUploader";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function AdminDeviImages() {
  const [devi, setDevi] = useState(null);
  const [maha, setMaha] = useState({});

  useEffect(() => {
    fetchAdminSettings().then((d) => {
      setDevi(d.devi_form_images || {});
      setMaha(d.mahavidya_images || {});
    }).catch(() => { setDevi({}); setMaha({}); });
  }, []);

  const saveDevi = async (slug, url) => {
    const next = { ...devi, [slug]: url };
    if (!url) delete next[slug];
    setDevi(next);
    try {
      await adminUpdateSettings({ devi_form_images: next });
      toast("Navadurga image saved", { description: "Republish (Sync) to update the live site." });
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
  };

  const saveMaha = async (slug, url) => {
    const next = { ...maha, [slug]: url };
    if (!url) delete next[slug];
    setMaha(next);
    try {
      await adminUpdateSettings({ mahavidya_images: next });
      toast("Mahavidya image saved", { description: "Republish (Sync) to update the live site." });
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
  };

  if (devi === null) return <p className="py-20 text-center text-ink-3">…</p>;

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-devi-images">
      <div className="mb-2 flex items-center gap-2.5">
        <Sparkles className="h-6 w-6 text-saffron" />
        <h1 className="font-serif-latin text-3xl text-ink">Devi Images</h1>
      </div>
      <p className="mb-8 max-w-2xl text-sm text-ink-3">
        Upload or replace the portrait for each of the nine Navadurga forms and the ten Dasha Mahavidya.
        Leave blank to use the built-in artwork. Changes save instantly; run Sync (Preview → Live) to publish.
      </p>

      <h2 className="mb-4 font-serif-latin text-xl text-ink">Navadurga — the nine forms</h2>
      <div className="space-y-4">
        {DEVI_FORMS.map((f) => (
          <div key={f.slug} className="border border-line bg-surface p-5" data-testid={`devi-img-row-${f.slug}`}>
            <div className="mb-3">
              <p className="font-deva text-lg text-ink deva-lh">{f.deva}</p>
              <p className="text-sm text-ink-2">Day {f.day} · {f.en}</p>
            </div>
            <ImageUploader label="Portrait" testid={`devi-img-${f.slug}`} value={devi[f.slug] || ""} onChange={(v) => saveDevi(f.slug, v)} hint="Recommended: tall portrait (3:4)" />
          </div>
        ))}
      </div>

      <h2 className="mb-4 mt-12 font-serif-latin text-xl text-ink">Dasha Mahavidya — the ten goddesses</h2>
      <div className="space-y-4">
        {MAHAVIDYAS.map((m) => (
          <div key={m.slug} className="border border-line bg-surface p-5" data-testid={`maha-img-row-${m.slug}`}>
            <div className="mb-3">
              <p className="font-deva text-lg text-ink deva-lh">{m.deva}</p>
              <p className="text-sm text-ink-2">{m.n}. {m.en} · {m.group === "ugra" ? "Ugra" : "Saumya"}</p>
            </div>
            <ImageUploader label="Portrait" testid={`maha-img-${m.slug}`} value={maha[m.slug] || ""} onChange={(v) => saveMaha(m.slug, v)} hint="Recommended: tall portrait (3:4)" />
          </div>
        ))}
      </div>
    </div>
  );
}
